import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { StatusBadge } from './StatusBadge';
import {
  LayoutDashboard, Users, PlusCircle, CalendarCheck, MessageSquare, BookOpen,
  Settings, ScrollText, Search, LogOut, ChevronDown, Menu, X, ClipboardCheck,
} from 'lucide-react';

const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['office', 'rector'] },
  { to: '/approvals', label: 'Pending Approvals', icon: ClipboardCheck, roles: ['rector'] },
  { to: '/applicants', label: 'Applicants', icon: Users, roles: ['office', 'rector'] },
  { to: '/applicants/new', label: 'New Case', icon: PlusCircle, roles: ['office'] },
  { to: '/tests', label: 'Admission Tests', icon: CalendarCheck, roles: ['office', 'rector'] },
  { to: '/communications', label: 'Communications', icon: MessageSquare, roles: ['office', 'rector'] },
  { to: '/syllabus', label: 'Syllabus Library', icon: BookOpen, roles: ['office', 'rector'] },
  { to: '/audit', label: 'Audit Log', icon: ScrollText, roles: ['rector'] },
  { to: '/settings', label: 'Settings', icon: Settings, roles: ['rector'] },
];

const ROLE_LABEL = { rector: 'Rector / Management', office: 'Office', parent: 'Parent' };

const BOTTOM_NAV = [
  { key: 'dashboard', to: '/dashboard', label: 'Home', icon: LayoutDashboard },
  { key: 'applicants', to: '/applicants', label: 'Applicants', icon: Users },
  { key: 'new-case', to: '/applicants/new', label: 'New Case', icon: PlusCircle, roles: ['office'] },
  { key: 'approvals', to: '/approvals', label: 'Approvals', icon: ClipboardCheck, roles: ['rector'] },
  { key: 'tests', to: '/tests', label: 'Tests', icon: CalendarCheck },
];

function GlobalSearch({ testid = 'global-search-input' }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get('/search', { params: { q } }); setResults(r.data); setOpen(true); }
      catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="relative w-full max-w-md">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
      <input
        data-testid={testid}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        placeholder="Search name, case ID, phone, class…"
        className="w-full rounded-full border border-[#E2E8F0] bg-[#F8FAFC] py-2 pl-9 pr-4 text-sm outline-none focus:border-action focus:bg-white focus:ring-2 focus:ring-action/20"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-2 w-[420px] max-w-[90vw] rounded-xl border border-[#E2E8F0] bg-white p-1.5 shadow-xl">
          {results.map((r) => (
            <button
              key={r.case_id}
              data-testid={`search-result-${r.case_number}`}
              onMouseDown={() => { navigate(`/cases/${r.case_id}`); setQ(''); setOpen(false); }}
              className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-[#F1F5F9]"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[#172033]">{r.applicant_name}</p>
                <p className="text-xs text-[#64748B]">{r.case_number} · {r.class_name}</p>
              </div>
              <StatusBadge status={r.status} size="sm" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user } = useAuth();
  return (
    <div className="flex items-center gap-3" data-testid="user-menu">
      <div className="hidden text-right sm:block">
        <span className="block text-xs font-semibold leading-tight text-[#172033]">{user?.name}</span>
        <span className="block text-[10px] uppercase tracking-wide text-[#64748B]">{ROLE_LABEL[user?.role]}</span>
      </div>
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-navy text-xs font-bold text-white">
        {(user?.name || 'U').slice(0, 1)}
      </span>
    </div>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const doLogout = useCallback(async () => { await logout(); navigate('/login', { replace: true }); }, [logout, navigate]);
  if (!user) return null;
  const nav = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 transform bg-navy transition-transform lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex h-16 items-center gap-2.5 border-b border-white/10 px-5">
          <img src="/hpmi-logo.webp" alt="HPMI crest" data-testid="sidebar-brand-logo" className="h-9 w-9 rounded-full bg-white p-0.5 ring-1 ring-gold/50" />
          <div>
            <p className="font-heading text-sm font-bold leading-tight text-white">Holy Palace</p>
            <p className="text-[10px] uppercase tracking-wider text-gold">Multipurpose Institute</p>
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setMobileOpen(false)}><X size={18} className="text-white/70" /></button>
        </div>
        <nav className="flex flex-col gap-1 p-3">
          {nav.map((n) => {
            const active = location.pathname === n.to || (n.to !== '/dashboard' && location.pathname.startsWith(n.to) && n.to !== '/applicants') || (n.to === '/applicants' && location.pathname === '/applicants');
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${active ? 'bg-white/10 font-semibold text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
              >
                <Icon size={18} />
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="absolute inset-x-0 bottom-0 p-3">
          <button data-testid="logout-button" onClick={doLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/70 hover:bg-white/5 hover:text-white">
            <LogOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 border-b border-[#E2E8F0] bg-white/90 backdrop-blur-md">
          <div className="flex h-16 items-center gap-1.5 px-3 sm:px-6">
            <button data-testid="mobile-menu-button" aria-label="Open menu" className="rounded-lg p-2 hover:bg-[#F1F5F9] lg:hidden" onClick={() => setMobileOpen(true)}><Menu size={20} className="text-[#475569]" /></button>
            <div className="hidden w-full max-w-md md:block"><GlobalSearch /></div>
            <button data-testid="mobile-search-toggle" aria-label="Search" className="rounded-lg p-2 text-[#475569] hover:bg-[#F1F5F9] md:hidden" onClick={() => setSearchOpen((v) => !v)}><Search size={20} /></button>
            <div className="ml-auto"><UserMenu /></div>
          </div>
          {searchOpen && <div className="border-t border-[#F1F5F9] px-4 py-2.5 md:hidden"><GlobalSearch testid="global-search-input-mobile" /></div>}
        </header>
        <main className="relative z-10 mx-auto max-w-[1400px] p-4 pb-24 sm:p-6 lg:pb-6">{children}</main>
      </div>
      <img
        src="/hpmi-logo.webp"
        alt=""
        aria-hidden
        data-testid="app-watermark"
        className="pointer-events-none fixed bottom-24 right-6 z-0 h-64 w-64 select-none opacity-[0.05] grayscale lg:bottom-8 lg:right-8"
      />

      {/* Mobile bottom navigation */}
      <nav data-testid="mobile-bottom-nav" className="fixed inset-x-0 bottom-0 z-30 border-t border-[#E2E8F0] bg-white/95 backdrop-blur-md lg:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex">
          {BOTTOM_NAV.filter((n) => !n.roles || n.roles.includes(user.role)).map((n) => {
            const Icon = n.icon;
            const active = n.key === 'dashboard' ? location.pathname === '/dashboard'
              : n.key === 'applicants' ? (location.pathname.startsWith('/applicants') && location.pathname !== '/applicants/new') || location.pathname.startsWith('/cases')
              : location.pathname.startsWith(n.to);
            return (
              <Link key={n.key} to={n.to} data-testid={`mobile-nav-${n.key}`}
                className={`flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] font-semibold ${active ? 'text-action' : 'text-[#64748B]'}`}>
                <Icon size={20} /> {n.label}
              </Link>
            );
          })}
          <button data-testid="mobile-nav-more" onClick={() => setMobileOpen(true)}
            className="flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] font-semibold text-[#64748B]">
            <Menu size={20} /> Menu
          </button>
        </div>
      </nav>
    </div>
  );
}
